import { useMutation } from "@apollo/client/react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";

import { DownloadBox } from "@/atoms";
import { constantsData, identifiersData, namesData, statusTypesData } from "@/data";
import { exportReleasesMutation } from "@/graph";
import { openAlert } from "@/redux/actions";
import { displayErrorHandler, getUniqueRandomId } from "@/utils";

const useExportReleases = () => {
    const [exporting, setExporting] = useState(false);

    const { t } = useTranslation();

    const dispatch = useDispatch();

    const [exportReleasesAction, { loading }] = useMutation(exportReleasesMutation);

    const {
        exportReleasesPending: exportReleasesPendingToastName,
        exportReleasesSuccess: exportReleasesSuccessToastName,
    } = namesData.toasts;

    const { toast: toastId } = identifiersData.ids;

    const exportReleases = async (ids) => {
        setExporting(true);

        try {
            await exportReleasesAction({
                onCompleted: (data) => {
                    setExporting(false);

                    const randomId = getUniqueRandomId();

                    toast.success(
                        () => (
                            <DownloadBox
                                id={`${exportReleasesSuccessToastName}_${toastId}_${randomId}`}
                                link={data.exportReleases}
                                message={t("messages.downloadMsgs.download", { entity: t("releases.name") })}
                            />
                        ),
                        {
                            closeButton: true,
                            toastId: `${exportReleasesSuccessToastName}_${toastId}_${randomId}`,
                        },
                    );

                    dispatch(
                        openAlert(
                            t("messages.exportMsgs.success", { entity: t("releases.name") }),
                            statusTypesData.success,
                        ),
                    );
                },
                variables: {
                    releasesIds: ids,
                    toCsv: true,
                },
            });
        } catch (err) {
            displayErrorHandler(
                err.message === constantsData.failedToFetch ? t("messages.networkFailure") : err.message,
                dispatch,
                openAlert,
            );
        } finally {
            toast.dismiss(`${exportReleasesPendingToastName}_${toastId}`);
        }
    };

    useEffect(
        () => {
            if (loading && exporting) {
                toast.info(() => t("messages.exportMsgs.pending", { entity: t("releases.name") }), {
                    toastId: `${exportReleasesPendingToastName}_${toastId}`,
                });
            }
        },
        [loading, exporting], //eslint-disable-line
    );

    return { exportReleases };
};

export default useExportReleases;
